import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { keyframes, styled } from "@pigment-css/react";

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const HeightBox = styled("div")(({ theme }) => ({
  overflow: "hidden",
  transition: `height ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
}));

const FadeContent = styled("div")(({ theme }) => ({
  animation: `${fadeIn} ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard} both`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
}));

export interface AnimateHeightProps {
  /** Changing this key fades the incoming content in, e.g. a "loading" / "empty" / "list" mode. */
  transitionKey: string;
  /** Applied to the clipping box, e.g. to re-add a horizontal inset that overflow:hidden would clip. */
  className?: string;
  children: ReactNode;
}

/**
 * Animates its height between content swaps so the container glides instead of jumping when its child
 * changes size. The live content height is measured and `transitionKey` fades
 * the incoming content in on each swap. Respects `prefers-reduced-motion`.
 */
export function AnimateHeight({ transitionKey, className, children }: AnimateHeightProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => setHeight(Math.ceil(el.getBoundingClientRect().height));
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <HeightBox className={className} style={{ height }}>
      <div ref={contentRef}>
        <FadeContent key={transitionKey}>{children}</FadeContent>
      </div>
    </HeightBox>
  );
}
